import { useState } from 'react'
import { X, Link2, Unlink, ExternalLink, Plus, Loader2, FileText, Image } from 'lucide-react'
import { Button } from '../Button'
import { Badge } from '../Badge'
import { DocumentPreview } from './DocumentPreview'
import { ExpenseAttachPicker } from './ExpenseAttachPicker'
import type { LibraryDocument, DocumentType, CreateExpenseRequest, ExpenseCategory } from '@/api/client'
import { api } from '@/api/client'

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  Receipt: 'Receipt',
  ProofOfPayment: 'Proof of Payment',
  LetterOfMedicalNecessity: 'LMN',
  ExplanationOfBenefits: 'EOB',
  Other: 'Other',
}

const DOC_TYPE_VARIANT: Record<DocumentType, 'navy' | 'gold' | 'green' | 'gray'> = {
  Receipt: 'navy',
  ProofOfPayment: 'green',
  LetterOfMedicalNecessity: 'gold',
  ExplanationOfBenefits: 'gray',
  Other: 'gray',
}

interface DocumentDetailViewProps {
  document: LibraryDocument
  onClose: () => void
  onDocumentUpdated: (doc: LibraryDocument) => void
  onDocumentDeleted: (docId: string) => void
}

type RightPanelMode = 'info' | 'create-expense' | 'link-existing'

export function DocumentDetailView({
  document,
  onClose,
  onDocumentUpdated,
  onDocumentDeleted,
}: DocumentDetailViewProps) {
  const [mode, setMode] = useState<RightPanelMode>('info')
  const [linking, setLinking] = useState(false)
  const [unlinking, setUnlinking] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [categories, setCategories] = useState<ExpenseCategory[]>([])
  const [categoriesLoaded, setCategoriesLoaded] = useState(false)

  // Create expense form state
  const [expenseForm, setExpenseForm] = useState<CreateExpenseRequest>({
    serviceDate: new Date().toISOString().split('T')[0],
    provider: '',
    description: '',
    amount: 0,
    expenseCategoryId: '',
  })
  const [creating, setCreating] = useState(false)

  const loadCategories = async () => {
    if (categoriesLoaded) return
    try {
      const cats = await api.getExpenseCategories()
      setCategories(cats || [])
      setCategoriesLoaded(true)
    } catch {}
  }

  const handleUnlink = async (expenseId: string) => {
    setUnlinking(expenseId)
    try {
      const updated = await api.unlinkDocument(document.id, expenseId)
      if (updated) onDocumentUpdated(updated)
    } catch (err) {
      console.error('Failed to unlink:', err)
    } finally {
      setUnlinking(null)
    }
  }

  const handleLinkToExpense = async (expenseId: string) => {
    setLinking(true)
    try {
      const updated = await api.linkDocument(document.id, expenseId)
      if (updated) onDocumentUpdated(updated)
      setMode('info')
    } catch (err) {
      console.error('Failed to link:', err)
    } finally {
      setLinking(false)
    }
  }

  const handleCreateExpense = async () => {
    if (!expenseForm.provider || !expenseForm.description || !expenseForm.expenseCategoryId) return
    setCreating(true)
    try {
      const expense = await api.createExpense(expenseForm)
      if (expense) {
        const updated = await api.linkDocument(document.id, expense.id)
        if (updated) onDocumentUpdated(updated)
      }
      setMode('info')
    } catch (err) {
      console.error('Failed to create expense:', err)
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.deleteLibraryDocument(document.id)
      onDocumentDeleted(document.id)
      onClose()
    } catch (err) {
      console.error('Failed to delete:', err)
    } finally {
      setDeleting(false)
    }
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    })

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)

  const isImage = document.contentType.startsWith('image/')

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div className="relative ml-auto w-full max-w-6xl bg-white flex shadow-xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 text-[#718096] hover:text-[#1B365D] rounded-lg hover:bg-[#F7FAFC]"
        >
          <X size={20} />
        </button>

        {/* Left panel - Preview (60%) */}
        <div className="w-3/5 border-r border-[#E2E8F0] flex flex-col">
          <div className="flex-1">
            <DocumentPreview
              documentId={document.id}
              contentType={document.contentType}
              fileName={document.fileName}
            />
          </div>
        </div>

        {/* Right panel - Context (40%) */}
        <div className="w-2/5 flex flex-col overflow-hidden">
          {/* Document metadata header */}
          <div className="p-4 border-b border-[#E2E8F0]">
            <div className="flex items-center gap-2 mb-2">
              {isImage ? <Image size={18} className="text-[#718096]" /> : <FileText size={18} className="text-[#718096]" />}
              <span className="text-[14px] font-semibold text-[#1B365D] truncate">{document.fileName}</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant={DOC_TYPE_VARIANT[document.documentType] || 'gray'} size="category">
                {DOC_TYPE_LABELS[document.documentType] || document.documentType}
              </Badge>
              <span className="text-[12px] text-[#A0AEC0]">{formatSize(document.fileSize)}</span>
              <span className="text-[12px] text-[#A0AEC0]">{formatDate(document.createdAt)}</span>
            </div>
            {document.description && (
              <p className="text-[13px] text-[#4A5568]">{document.description}</p>
            )}
          </div>

          {/* Right panel content */}
          <div className="flex-1 overflow-y-auto p-4">
            {mode === 'info' && (
              <div className="space-y-4">
                {/* Linked expenses */}
                <div>
                  <h3 className="text-[13px] font-semibold text-[#1B365D] mb-2">
                    Linked Expenses ({document.linkedExpenses.length})
                  </h3>
                  {document.linkedExpenses.length === 0 ? (
                    <p className="text-[13px] text-[#A0AEC0]">Not linked to any expenses</p>
                  ) : (
                    <div className="space-y-2">
                      {document.linkedExpenses.map((expense) => (
                        <div
                          key={expense.id}
                          className="flex items-center gap-2 p-3 rounded-lg border border-[#E2E8F0] bg-white"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium text-[#1B365D] truncate">{expense.provider}</div>
                            <div className="text-[11px] text-[#A0AEC0]">
                              {formatCurrency(expense.amount)} &middot; {formatDate(expense.serviceDate + 'T00:00:00')}
                            </div>
                          </div>
                          <a
                            href={`/expenses`}
                            className="p-1.5 text-[#718096] hover:text-[#1B365D] rounded hover:bg-[#EDF2F7]"
                            title="View expense"
                          >
                            <ExternalLink size={14} />
                          </a>
                          <button
                            onClick={() => handleUnlink(expense.id)}
                            disabled={unlinking === expense.id}
                            className="p-1.5 text-[#718096] hover:text-red-500 rounded hover:bg-red-50"
                            title="Unlink"
                          >
                            {unlinking === expense.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Unlink size={14} />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <Button
                    variant="primary"
                    size="small"
                    className="w-full"
                    onClick={() => {
                      loadCategories()
                      setMode('create-expense')
                    }}
                  >
                    <Plus size={14} className="mr-1.5" />
                    Create New Expense
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    className="w-full"
                    onClick={() => setMode('link-existing')}
                  >
                    <Link2 size={14} className="mr-1.5" />
                    Link to Existing Expense
                  </Button>
                </div>

                {/* Delete */}
                <div className="pt-4 border-t border-[#E2E8F0]">
                  <Button
                    variant="destructive"
                    size="small"
                    className="w-full"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                    Delete Document
                  </Button>
                </div>
              </div>
            )}

            {mode === 'create-expense' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-[13px] font-semibold text-[#1B365D]">Create New Expense</h3>
                  <button
                    onClick={() => setMode('info')}
                    className="text-[12px] text-[#718096] hover:text-[#1B365D]"
                  >
                    Cancel
                  </button>
                </div>

                <div>
                  <label className="text-[12px] font-medium text-[#4A5568] block mb-1">Provider</label>
                  <input
                    type="text"
                    value={expenseForm.provider}
                    onChange={(e) => setExpenseForm({ ...expenseForm, provider: e.target.value })}
                    className="w-full text-[13px] border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#4A5568] focus:outline-none focus:border-[#1B365D]"
                    placeholder="e.g., Dr. Smith"
                  />
                </div>

                <div>
                  <label className="text-[12px] font-medium text-[#4A5568] block mb-1">Description</label>
                  <input
                    type="text"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                    className="w-full text-[13px] border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#4A5568] focus:outline-none focus:border-[#1B365D]"
                    placeholder="e.g., Annual checkup"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[12px] font-medium text-[#4A5568] block mb-1">Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={expenseForm.amount || ''}
                      onChange={(e) => setExpenseForm({ ...expenseForm, amount: parseFloat(e.target.value) || 0 })}
                      className="w-full text-[13px] border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#4A5568] focus:outline-none focus:border-[#1B365D]"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="text-[12px] font-medium text-[#4A5568] block mb-1">Service Date</label>
                    <input
                      type="date"
                      value={expenseForm.serviceDate}
                      onChange={(e) => setExpenseForm({ ...expenseForm, serviceDate: e.target.value })}
                      className="w-full text-[13px] border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#4A5568] focus:outline-none focus:border-[#1B365D]"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[12px] font-medium text-[#4A5568] block mb-1">Category</label>
                  <select
                    value={expenseForm.expenseCategoryId}
                    onChange={(e) => setExpenseForm({ ...expenseForm, expenseCategoryId: e.target.value })}
                    className="w-full text-[13px] border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#4A5568] focus:outline-none focus:border-[#1B365D]"
                  >
                    <option value="">Select category...</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <Button
                  variant="primary"
                  size="small"
                  className="w-full"
                  onClick={handleCreateExpense}
                  disabled={creating || !expenseForm.provider || !expenseForm.description || !expenseForm.expenseCategoryId}
                >
                  {creating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                  Create & Link
                </Button>
              </div>
            )}

            {mode === 'link-existing' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[13px] font-semibold text-[#1B365D]">Link to Existing Expense</h3>
                  <button
                    onClick={() => setMode('info')}
                    className="text-[12px] text-[#718096] hover:text-[#1B365D]"
                  >
                    Cancel
                  </button>
                </div>
                {linking && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 size={20} className="text-[#1B365D] animate-spin" />
                  </div>
                )}
                <ExpenseAttachPicker
                  linkedExpenseIds={document.linkedExpenses.map(e => e.id)}
                  onSelect={handleLinkToExpense}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
